import React, { useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import {
  FaCloudUploadAlt,
  FaTrash,
  FaArrowLeft,
  FaArrowRight,
  FaStar,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { resolveAssetUrl } from "../utils/assets";
import "./ProductImageManager.css";

const isBlobUrl = (url) => typeof url === "string" && url.startsWith("blob:");

/**
 * Product photos stay local until the product form is saved.
 * Upload/delete hit the server only from the parent submit handler.
 */
const ProductImageManager = ({ images = [], onChange, pendingFilesRef }) => {
  const inputRef = useRef(null);
  const [selecting, setSelecting] = useState(false);

  const update = (next) => onChange(next);

  const handleFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    setSelecting(true);
    try {
      const nextUrls = [];
      for (const file of files) {
        if (!file.type?.startsWith("image/")) {
          toast.error(`${file.name} is not an image`);
          continue;
        }
        const blobUrl = URL.createObjectURL(file);
        pendingFilesRef?.current?.set(blobUrl, file);
        nextUrls.push(blobUrl);
      }
      if (!nextUrls.length) return;
      update([...(images || []), ...nextUrls]);
      toast.success(
        `${nextUrls.length} image(s) selected — click Save to keep them`
      );
    } finally {
      setSelecting(false);
    }
  };

  const move = (index, direction) => {
    const next = [...images];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    update(next);
  };

  const removeAt = (index) => {
    const url = images[index];
    if (isBlobUrl(url)) {
      URL.revokeObjectURL(url);
      pendingFilesRef?.current?.delete(url);
    }
    update(images.filter((_, i) => i !== index));
    toast.info("Image removed — click Save to apply");
  };

  const setAsCover = (index) => {
    if (index === 0) return;
    const next = [...images];
    const [picked] = next.splice(index, 1);
    next.unshift(picked);
    update(next);
    toast.info("Cover updated — click Save to apply");
  };

  return (
    <div className="pim">
      <div className="pim-header">
        <div>
          <h4>Product photos</h4>
          <p>
            Select, reorder, or remove images. Changes are stored only when you
            save the product.
          </p>
        </div>
        <div className="pim-actions">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            hidden
            onChange={handleFiles}
          />
          <Button
            type="button"
            className="pim-upload-btn"
            disabled={selecting}
            onClick={() => inputRef.current?.click()}
          >
            {selecting ? (
              <>
                <Spinner animation="border" size="sm" /> Selecting...
              </>
            ) : (
              <>
                <FaCloudUploadAlt /> Upload photos
              </>
            )}
          </Button>
        </div>
      </div>

      {!images?.length ? (
        <button
          type="button"
          className="pim-dropzone"
          disabled={selecting}
          onClick={() => inputRef.current?.click()}
        >
          <FaCloudUploadAlt />
          <span>Click to select product photos</span>
          <small>JPEG, PNG, WEBP, GIF · up to 5MB each · max 10 files</small>
        </button>
      ) : (
        <div className="pim-grid">
          {images.map((url, index) => (
            <div className="pim-card" key={`${url}-${index}`}>
              <div className="pim-preview">
                <img src={resolveAssetUrl(url)} alt={`Product ${index + 1}`} />
                {index === 0 && <span className="pim-cover-badge">Cover</span>}
              </div>
              <div className="pim-toolbar">
                <button
                  type="button"
                  title="Move left"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <FaArrowLeft />
                </button>
                <button
                  type="button"
                  title="Move right"
                  disabled={index === images.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <FaArrowRight />
                </button>
                <button
                  type="button"
                  title="Set as cover"
                  disabled={index === 0 || selecting}
                  onClick={() => setAsCover(index)}
                >
                  <FaStar />
                </button>
                <button
                  type="button"
                  className="danger"
                  title="Delete"
                  onClick={() => removeAt(index)}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductImageManager;
